import { el, clear } from '../dom.js';
import { expand } from '../../server/lib/recurrence.js';
import { shiftDate as shift } from '../../server/lib/dates.js';
import { t, describeRecurrence, formatDate, WEEKDAY_VALUES } from '../i18n.js';

// A guided sentence rather than a preset list. The sentence composes to
// exactly the four rule shapes the model can store, so the control cannot
// offer anything that will not save — and it shares its vocabulary with
// describeRecurrence, so the editor and the summary never drift apart.
//
// The preview is the real safety net: a publisher verifies by reading dates,
// not by trusting a label. It runs the same expand() the materialiser uses,
// so what it shows is what will be written.
export function recurrenceField({ rule, startDate, onChange }) {
  const state = normalise(rule);

  // A single date is the default and "repeats" is a toggle, not an option
  // inside the sentence. Recurrence is the hardest control in the product, and
  // routing every one-off through it taxes the common case for the rare one in
  // the middle of the flow — which is where a wizard loses people. The toggle
  // owns the `once` shape, so the select cannot offer a state that contradicts
  // it, the same reason the setting is a pair of radios.
  const repeats = el('input', { id: 'rec-repeats', type: 'checkbox' });
  repeats.checked = state.freq !== 'once';

  const freq = select('rec-freq', [
    ['weekly', t.freqWeekly],
    ['monthly', t.freqMonthly],
  ], state.freq === 'monthly' ? 'monthly' : 'weekly');

  const interval = select('rec-interval', [
    ['1', t.everyWeek], ['2', t.every2Weeks], ['3', t.every3Weeks], ['4', t.every4Weeks],
  ], String(state.interval ?? 1));

  const weekday = select('rec-weekday', WEEKDAY_VALUES, state.byday?.[0] ?? 'FR');

  const monthlyMode = select('rec-monthmode', [
    ['byday', t.byWeekday],
    ['bymonthday', t.byDate],
  ], state.bymonthday ? 'bymonthday' : 'byday');

  const setpos = select('rec-setpos', [
    ['1', t.first], ['2', t.second], ['3', t.third], ['4', t.fourth], ['-1', t.last],
  ], String(state.bysetpos ?? 1));

  const monthday = select('rec-monthday',
    Array.from({ length: 31 }, (_, i) => [String(i + 1), `${i + 1}.`]),
    String(state.bymonthday ?? 1));

  const until = el('input', { id: 'rec-until', type: 'date', value: state.until ?? '' });

  const weeklyBits = el('span', { class: 'rec-bits' }, [interval, labelled(t.onWeekday, weekday)]);
  const monthlyBits = el('span', { class: 'rec-bits' }, [monthlyMode, setpos, weekday.cloneNode(true), monthday]);
  const preview = el('ul', { class: 'rec-preview' });
  const summary = el('p', { class: 'rec-summary' });

  // The monthly branch needs its own weekday control, or switching frequency
  // would move the same node between two parents and lose its value.
  const monthlyWeekday = monthlyBits.children[2];
  monthlyWeekday.id = 'rec-weekday-monthly';
  monthlyWeekday.value = state.byday?.[0] ?? 'FR';

  const row = el('div', { class: 'rec-row' }, [
    el('span', {}, t.repeats),
    freq,
    weeklyBits,
    monthlyBits,
    el('span', { class: 'rec-until' }, [labelled(t.untilLabel, until)]),
  ]);

  const root = el('div', { class: 'field rec' }, [
    el('div', { class: 'field field-check' }, [
      repeats, el('label', { for: 'rec-repeats' }, t.repeatsToggle),
    ]),
    row,
    summary,
    preview,
  ]);

  for (const control of [repeats, freq, interval, weekday, monthlyMode, setpos, monthlyWeekday, monthday, until]) {
    control.addEventListener('change', update);
  }

  update();

  function read() {
    if (!repeats.checked) return { freq: 'once' };
    const value = freq.value;

    const common = until.value ? { until: until.value } : {};

    if (value === 'weekly') {
      return { freq: 'weekly', interval: Number(interval.value), byday: [weekday.value], ...common };
    }
    return monthlyMode.value === 'bymonthday'
      ? { freq: 'monthly', bymonthday: Number(monthday.value), ...common }
      : { freq: 'monthly', byday: [monthlyWeekday.value], bysetpos: Number(setpos.value), ...common };
  }

  function update() {
    const value = freq.value;
    // Everything the sentence is made of disappears with the toggle, not just
    // the parts that do not apply: a single date has no rule to read, and a
    // ten-date preview under it would be describing something else.
    row.hidden = !repeats.checked;
    summary.hidden = !repeats.checked;
    preview.hidden = !repeats.checked;

    weeklyBits.hidden = value !== 'weekly';
    monthlyBits.hidden = value !== 'monthly';
    setpos.hidden = monthlyMode.value === 'bymonthday';
    monthlyWeekday.hidden = monthlyMode.value === 'bymonthday';
    monthday.hidden = monthlyMode.value !== 'bymonthday';

    const current = read();
    summary.textContent = describeRecurrence(current);
    renderPreview(current);
    onChange?.(current);
  }

  function renderPreview(current) {
    clear(preview);
    if (!repeats.checked) return;
    const from = root.dataset.startDate || startDate;
    if (!from) return;

    let dates = [];
    try {
      dates = expand({
        startDate: from,
        rule: current,
        from,
        to: shift(from, 800),
        limit: 10,
      });
    } catch {
      // An impossible rule (31 February) simply has no dates to show. The
      // summary above still describes what was asked for.
    }

    preview.append(el('li', { class: 'rec-preview-label' }, t.nextDates));
    if (dates.length === 0) {
      preview.append(el('li', { class: 'rec-preview-none' }, t.noDates));
      return;
    }
    for (const date of dates.slice(0, 10)) {
      preview.append(el('li', {}, formatDate(date)));
    }
  }

  return {
    node: root,
    value: read,
    // The preview is anchored to the series start date, so it has to be
    // recomputed when that field changes.
    setStartDate(date) {
      root.dataset.startDate = date;
      update();
    },
  };
}

function normalise(rule) {
  if (!rule || typeof rule !== 'object') return { freq: 'once' };
  return rule;
}

function select(id, options, value) {
  const node = el('select', { id }, options.map(([v, label]) => el('option', { value: v }, label)));
  node.value = value;
  return node;
}

function labelled(text, control) {
  return el('span', { class: 'rec-inline' }, [el('span', {}, text), control]);
}
